import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { coreApi } from '../api/axios';
import { useDocumentStatuses } from './DocumentStatusesContext';
import { getDocumentStatusColor } from '../config/documentStatusColors';
import { createStaticListCache } from '../utils/staticListCache';
import { useLatestRequestGuard } from '../hooks/useLatestRequestGuard';

const repairCategoriesStaticCache = createStaticListCache();

/** Сброс in-memory кэша категорий ремонта (logout, смена пользователя, тесты). */
export function invalidateRepairCategoriesCache() {
  repairCategoriesStaticCache.invalidate();
}

// Функция для получения цвета категории ремонта
const getCategoryColor = (categoryCode) => {
  if (!categoryCode) return 'default';

  const lowerCode = categoryCode.toLowerCase();

  if (lowerCode.includes('warranty')) return 'success';
  if (lowerCode.includes('paid')) return 'primary';
  if (lowerCode.includes('internal')) return 'info';
  if (lowerCode.includes('external')) return 'warning';

  return 'default';
};

// Список статусов "только для чтения" (read_only)
const READ_ONLY_STATUSES = [
  'REPAIR_UNDER_APPROVAL',    // Соответствует RA - на утверждении
  'REPAIR_APPROVED',          // Соответствует AA - утвержден
  'REPAIR_ISSUED_TO_CLIENT',  // Соответствует SC - выдан
  'REPAIR_CLOSED',            // Соответствует CL - завершен
  'REPAIR_REJECTED'           // Соответствует RJ - отклонен
];

const RepairsContext = createContext();

export function RepairsProvider({ children }) {
  const { statusMap: fullStatusMap, loading: statusesLoading, error: statusesError } = useDocumentStatuses();
  const [repairCategories, setRepairCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const { begin: beginCategoriesLoad, isCurrent: isCategoriesLoadCurrent } = useLatestRequestGuard();

  const statusMap = useMemo(() => {
    if (!fullStatusMap || typeof fullStatusMap !== 'object') return {};
    return Object.fromEntries(
      Object.entries(fullStatusMap).filter(([code]) => code.startsWith('REPAIR_'))
    );
  }, [fullStatusMap]);

  useEffect(() => {
    let cancelled = false;
    const requestId = beginCategoriesLoad();
    setCategoriesLoading(true);

    repairCategoriesStaticCache
      .getOrLoad(async () => {
        const response = await coreApi.get('/operations/repair-categories');
        if (response.data && Array.isArray(response.data)) {
          return response.data.filter((category) => category.code !== 'SERVICE');
        }
        return [];
      })
      .then((filteredCategories) => {
        if (cancelled || !isCategoriesLoadCurrent(requestId)) return;
        setRepairCategories(filteredCategories);
        setCategoriesError(null);
      })
      .catch((err) => {
        if (cancelled || !isCategoriesLoadCurrent(requestId)) return;
        console.error('Ошибка при загрузке категорий ремонта:', err);
        setCategoriesError('Ошибка при загрузке категорий ремонта');
      })
      .finally(() => {
        if (cancelled || !isCategoriesLoadCurrent(requestId)) return;
        setCategoriesLoading(false);
      });

    return () => {
      cancelled = true;
      beginCategoriesLoad();
    };
  }, [beginCategoriesLoad, isCategoriesLoadCurrent]);

  const getCategoryNameByCode = useCallback((categoryCode) => {
    const foundCategory = repairCategories.find((cat) => cat.code === categoryCode);
    if (foundCategory && foundCategory.name) return foundCategory.name;
    return categoryCode;
  }, [repairCategories]);

  const getStatusNameByCode = useCallback((statusCode) => {
    if (statusMap[statusCode]?.name) return statusMap[statusCode].name;
    return statusCode;
  }, [statusMap]);

  const getStatusColorByCode = useCallback((statusCode) => {
    if (statusMap[statusCode]?.color) return statusMap[statusCode].color;
    return getDocumentStatusColor(statusCode);
  }, [statusMap]);

  const isReadOnly = useCallback(
    (statusCode) => READ_ONLY_STATUSES.includes(statusCode),
    []
  );

  const getReadOnlyStatuses = useCallback(() => [...READ_ONLY_STATUSES], []);

  const error = statusesError || categoriesError;

  const value = useMemo(
    () => ({
      statusMap,
      repairCategories,
      statusesLoading,
      categoriesLoading,
      error,
      getStatusName: getStatusNameByCode,
      getStatusColor: getStatusColorByCode,
      getCategoryName: getCategoryNameByCode,
      getCategoryColor,
      isReadOnly,
      getReadOnlyStatuses,
    }),
    [
      statusMap,
      repairCategories,
      statusesLoading,
      categoriesLoading,
      error,
      getStatusNameByCode,
      getStatusColorByCode,
      getCategoryNameByCode,
      isReadOnly,
      getReadOnlyStatuses,
    ]
  );

  return (
    <RepairsContext.Provider value={value}>
      {children}
    </RepairsContext.Provider>
  );
}

export function useRepairs() {
  const context = useContext(RepairsContext);
  if (context === undefined) {
    throw new Error('useRepairs must be used within a RepairsProvider');
  }
  return context;
}
